import type { Tutorial } from '../types'

const IMG = '/images/tutorials/mobile/collaborator-messaging'

export const collaboratorMessagingTutorial: Tutorial = {
  slug: 'collaborator-messaging',
  title: 'Collaborator Messaging',
  subtitle: 'One thread for chat, reactions, and goal comments',
  date: '2026-07-09',
  category: 'Sharing & Support',
  platform: 'mobile',
  tags: ['Messaging', 'Goals Partners', 'reactions', 'comments', 'crisis support'],
  description:
    'Chat, reactions, and partner comments all live in one unified thread — visible from the partnership screen and the app-wide Messages inbox.',
  highlights: [
    'One conversation, two doors: partnership screen and Messages inbox stay in sync.',
    'Reactions render as compact status lines, not speech bubbles.',
    'Crisis-support detection is unchanged — same hotline prompt when it\'s needed.',
  ],
  coverImage: `${IMG}/01_unified_thread.png`,
  sections: [
    {
      heading: 'One thread, not two',
      paragraphs: [
        'Opening a partnership shows the goal owner\'s scoped goal and a single conversation below it — every comment, reaction, and system reminder in chronological order, same as any chat.',
      ],
      image: {
        src: `${IMG}/01_unified_thread.png`,
        alt: 'Unified partnership messaging thread',
        caption: 'Unified partnership thread',
      },
    },
    {
      heading: 'The same conversation, wherever you open it',
      paragraphs: [
        'A message sent from the partnership screen shows up immediately in the app-wide **Messages** inbox too, with the right preview text and timestamp. Reply from Messages, and it appears back on the partnership screen.',
      ],
      image: {
        src: `${IMG}/02_messages_inbox.png`,
        alt: 'Messages inbox showing partnership conversation',
        caption: 'Messages inbox shows the partnership conversation',
      },
    },
    {
      heading: 'Bidirectional sync',
      paragraphs: [
        'One conversation, two doors into it — no separate copy, no wondering which screen a given thread lives on.',
      ],
      image: {
        src: `${IMG}/03_bidirectional_sync.png`,
        alt: 'Bidirectional sync between partnership screen and Messages',
        caption: 'Bidirectional sync between screens',
      },
    },
    {
      heading: 'The partner\'s view: brief, goal, and quick reactions',
      paragraphs: [
        'From the partner\'s side, the same screen carries the **Coach brief**, the scoped goal card with one-tap reaction emoji, and **Leave a comment**.',
      ],
      image: {
        src: `${IMG}/04_partner_view_scoped_goal.png`,
        alt: 'Partner view with Coach brief and scoped goal card',
        caption: 'Partner view with Coach brief and scoped goal',
      },
    },
    {
      heading: 'Reactions and comments read differently from chat',
      paragraphs: [
        'A quick reaction isn\'t a chat message — it\'s a signal, and it looks like one: a compact, centered status line, not a speech bubble.',
        '**Leave a comment** opens a blank composer so the partner writes encouragement in their own words.',
      ],
      image: {
        src: `${IMG}/05_reaction_status_line.png`,
        alt: 'Reaction rendered as a compact status line',
        caption: 'Reaction renders as a status line',
      },
    },
    {
      heading: 'Comments and safety',
      paragraphs: [
        'Once sent, a comment shows up in the thread as a normal chat bubble (the partner\'s actual words). Legacy nudge status lines may still appear for older posts.',
        'The crisis-support check that watches partner messages for language that needs real help hasn\'t changed. It still fires the same hotline prompt the instant it\'s needed.',
      ],
      image: {
        src: `${IMG}/08_crisis_hotline.png`,
        alt: 'Crisis hotline dialog for partner messages',
        caption: 'Crisis hotline dialog',
      },
    },
  ],
  whyMatters:
    'Splitting "activity" from "messages" is how most accountability apps end up with two half-empty tabs nobody checks consistently. Livewello keeps everything in one place, uses lightweight status lines for low-effort signals, and leaves safety-critical behavior like crisis detection completely untouched.',
  relatedSlugs: ['accountability-partners', 'coach-copilot'],
  meta: {
    description:
      'Unified collaborator messaging: one thread for chat, reactions, and partner comments — synced across partnership and Messages.',
    ogImage: `${IMG}/01_unified_thread.png`,
  },
}
